import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ScrollView,
  Image,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAuth } from '../../contexts/AuthContext';
import { showGuestAlert } from '../../utils/guestReadOnlyAlert';
import { ensureChat } from '../../services/chatService';
import { getDonationListings, getEvents } from '../../services/communityService';
import { subscribeForumPosts } from '../../services/forumService';
import { searchSameBirthCohort } from '../../services/searchService';
import { getBlockedUserIdsSet } from '../../services/userService';
import { cloudinaryDisplayUrl } from '../../services/cloudinaryService';
import type { CommunityEventDoc, MarketplaceListingDoc } from '../../types/community';
import type { ForumPostDoc } from '../../types/forum';
import type { UserProfile } from '../../types/models';
import { formatBabyAgeMonthsLabel, getBabyAgeInMonths } from '../../utils/babyAge';
import { impactLight } from '../../utils/haptics';
import {
  isForumPostLocallyHidden,
  subscribeForumLocalHideChanges,
} from '../../utils/forumReportLocalHide';
import { HomeScreenSkeleton } from '../../components/skeletons/ScreenSkeletons';
import type { MainStackParamList, MainTabParamList } from '../../navigation/types';

function formatFeedPostDate(createdAt: ForumPostDoc['createdAt']): string {
  try {
    const d = createdAt?.toDate?.() ?? new Date();
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

function formatFeedEventWhen(eventAt: CommunityEventDoc['eventAt']): string {
  try {
    const d = eventAt.toDate();
    return d.toLocaleString('tr-TR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function HomeScreen() {
  const insets = useSafeAreaInsets();
  const { user, profile, logout } = useAuth();
  const isGuest = Boolean(user?.isAnonymous);
  const tabNavigation = useNavigation<BottomTabNavigationProp<MainTabParamList, 'Home'>>();
  const [cohort, setCohort] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rawFeedPosts, setRawFeedPosts] = useState<ForumPostDoc[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<CommunityEventDoc[]>([]);
  const [donations, setDonations] = useState<MarketplaceListingDoc[]>([]);
  const [hideForumEpoch, bumpForumHide] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const unsub = subscribeForumPosts(setRawFeedPosts, undefined, 8);
    return unsub;
  }, []);

  useEffect(() => subscribeForumLocalHideChanges(() => bumpForumHide()), []);

  const blockedUserIds = useMemo(() => getBlockedUserIdsSet(profile), [profile?.blockedUsers]);

  const feedPosts = useMemo(
    () =>
      rawFeedPosts.filter(
        p => !blockedUserIds.has(p.authorId) && !isForumPostLocallyHidden(p.id),
      ),
    [rawFeedPosts, blockedUserIds, hideForumEpoch],
  );

  const cohortVisible = useMemo(
    () => cohort.filter(u => !blockedUserIds.has(u.id)),
    [cohort, blockedUserIds],
  );

  const donationsVisible = useMemo(
    () => donations.filter(d => !blockedUserIds.has(d.sellerId)),
    [donations, blockedUserIds],
  );

  const upcomingEventsVisible = useMemo(
    () => upcomingEvents.filter(e => !blockedUserIds.has(e.createdBy)),
    [upcomingEvents, blockedUserIds],
  );

  const loadUpcomingEvents = useCallback(async () => {
    const c = profile?.city?.trim();
    if (!c) {
      setUpcomingEvents([]);
      return;
    }
    try {
      const list = await getEvents(c, profile?.district?.trim() || undefined);
      const now = Date.now();
      const future = list
        .filter(ev => {
          try {
            return ev.eventAt.toDate().getTime() >= now - 60_000;
          } catch {
            return false;
          }
        })
        .slice(0, 3);
      setUpcomingEvents(future);
    } catch {
      setUpcomingEvents([]);
    }
  }, [profile?.city, profile?.district]);

  const loadDonations = useCallback(async () => {
    const c = profile?.city?.trim();
    if (!c) {
      setDonations([]);
      return;
    }
    try {
      const list = await getDonationListings(c, profile?.district?.trim() || undefined);
      setDonations(list.slice(0, 12));
    } catch {
      setDonations([]);
    }
  }, [profile?.city, profile?.district]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!user?.uid || !profile?.babyBirthDate) {
        setCohort([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const list = await searchSameBirthCohort(profile.babyBirthDate, user.uid);
        setCohort(list);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user?.uid, profile?.babyBirthDate],
  );

  useFocusEffect(
    useCallback(() => {
      load();
      void loadUpcomingEvents();
      void loadDonations();
    }, [load, loadUpcomingEvents, loadDonations]),
  );

  const months = profile?.babyBirthDate ? getBabyAgeInMonths(profile.babyBirthDate) : null;

  const openChatWith = useCallback(
    async (peer: UserProfile) => {
      if (user?.isAnonymous === true) {
        showGuestAlert(logout);
        return;
      }
      if (!user?.uid) {
        return;
      }
      const { chatId } = await ensureChat(user.uid, peer.id);
      const peerName = peer.displayName?.trim() || 'Annedaş';
      tabNavigation
        .getParent<NativeStackNavigationProp<MainStackParamList>>()
        ?.navigate('Chat', {
          chatId,
          otherUserId: peer.id,
          peerName,
        });
    },
    [logout, tabNavigation, user?.isAnonymous, user?.uid],
  );

  function renderHeader() {
    const greetingName = profile?.displayName?.trim();
    return (
 <View className="mb-5 flex-row items-start justify-between gap-3">
 <View className="min-w-0 flex-1">
 <Text className="text-2xl font-bold leading-tight text-gray-800 dark:text-zinc-100">
            Merhaba{greetingName ? `, ${greetingName}` : ''}
          </Text>
 <Text className="mt-2 text-sm leading-5 text-gray-500 dark:text-zinc-400">
            Bebeğin {months !== null ? formatBabyAgeMonthsLabel(months) : '—'} — aynı ayda doğan ailelerle
            burada tanışabilirsin.
          </Text>
        </View>
        <TouchableOpacity
          activeOpacity={0.85}
          accessibilityLabel={isGuest ? 'Kayıt ol veya giriş yap' : 'Profil ve ayarlar'}
          onPress={() => {
            if (isGuest) {
              void logout();
              return;
            }
            tabNavigation
              .getParent<NativeStackNavigationProp<MainStackParamList>>()
              ?.navigate('ProfileEdit');
          }}
 className="rounded-2xl border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">
            {isGuest ? 'Kayıt Ol / Giriş Yap' : '⚙️ Profil'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  function renderEmpty() {
    if (!profile?.babyBirthDate) {
      return (
 <View className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-center text-sm leading-6 text-gray-500 dark:text-zinc-400">
            {isGuest
              ? 'Misafir olarak topluluğun sorularını ve buluşmalarını gezebilirsin. Kişiselleştirilmiş akış için ücretsiz kayıt olman yeterli.'
              : 'Profilinde bebek doğum tarihi olmadan akış gösterilemez.'}
          </Text>
        </View>
      );
    }
    return (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900 px-5 py-6 dark:border-zinc-800 dark:bg-zinc-900/50">
 <Text className="mb-2 text-center text-base font-bold text-gray-800 dark:text-zinc-100">
          Henüz aynı ayda bebeği olan bir Annedaş yok
        </Text>
 <Text className="text-center text-xs leading-5 text-gray-500 dark:text-zinc-400">
          Seninle aynı ayda bebeği olan Annedaş&apos;ları bekliyoruz. İstersen{' '}
 <Text className="font-semibold text-zinc-600">Arama</Text> sekmesinden Genel Arama
          yapabilirsin.
        </Text>
      </View>
    );
  }

  if (loading && cohort.length === 0 && profile?.babyBirthDate) {
    return (
 <View className="flex-1 bg-white dark:bg-black" style={{ paddingTop: insets.top }}>
        <HomeScreenSkeleton />
      </View>
    );
  }

  return (
    <ScrollView
 className="flex-1 bg-white dark:bg-black"
      style={{ paddingTop: insets.top }}
      contentContainerStyle={{ paddingBottom: 120 + insets.bottom }}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={() => {
            setRefreshing(true);
            load({ silent: true });
            void loadUpcomingEvents();
            void loadDonations();
          }}
          tintColor="#18181b"
        />
      }
      showsVerticalScrollIndicator={false}>
 <View className="px-5 pt-4">{renderHeader()}</View>

 <View className="px-5 pb-2">
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={() => {
            void impactLight();
            tabNavigation
              .getParent<NativeStackNavigationProp<MainStackParamList>>()
              ?.navigate('Playdates');
          }}
          className="overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="flex-row items-center justify-between gap-3">
 <View className="min-w-0 flex-1">
 <Text className="text-base font-bold text-zinc-900 dark:text-zinc-100">
                Oyun Arkadaşı Bul
              </Text>
 <Text className="mt-1 text-xs leading-5 text-zinc-600 dark:text-zinc-500">
                İlçende, bebeğinle aynı yaş civarındaki annelerle tanış
              </Text>
            </View>
 <Text className="text-2xl">🧸</Text>
          </View>
        </TouchableOpacity>
      </View>

      {cohortVisible.length === 0 ? (
 <View className="px-5">{renderEmpty()}</View>
      ) : (
        <View className="mt-2">
          <Text className="mb-4 mt-8 px-5 text-sm font-bold text-zinc-900 dark:text-zinc-100">
            Aynı ay doğumlu Annedaş&apos;lar
          </Text>
          <FlatList
            horizontal
            data={cohortVisible}
            keyExtractor={item => item.id}
            showsHorizontalScrollIndicator={false}
            nestedScrollEnabled
            contentContainerStyle={{ paddingLeft: 20, paddingRight: 20 }}
            renderItem={({ item }) => {
              const m = getBabyAgeInMonths(item.babyBirthDate);
              return (
                <View className="mr-3 w-[11.5rem] rounded-xl border border-zinc-200 bg-white px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900">
                  <Text
                    className="text-sm font-semibold text-gray-800 dark:text-zinc-100"
                    numberOfLines={2}>
                    {item.displayName}
                  </Text>
                  <Text className="mt-1.5 text-xs leading-4 text-gray-500 dark:text-zinc-400">
                    Bebek {formatBabyAgeMonthsLabel(m)}
                  </Text>
                  {item.city ? (
                    <Text className="mt-1 text-xs leading-4 text-gray-400 dark:text-zinc-500">
                      {item.city}
                      {item.district ? ` · ${item.district}` : ''}
                    </Text>
                  ) : null}
                  <View className="mt-3 items-end">
                    <TouchableOpacity
                      activeOpacity={0.85}
                      onPress={() => {
                        void impactLight();
                        if (user?.isAnonymous === true) {
                          showGuestAlert(logout);
                          return;
                        }
                        void openChatWith(item);
                      }}
                      className="rounded-lg border border-zinc-900 bg-zinc-900 px-3 py-2 active:opacity-90 dark:border-zinc-50 dark:bg-zinc-50">
                      <Text className="text-center text-xs font-semibold text-white dark:text-zinc-950">
                        Mesaj
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            }}
          />
        </View>
      )}

      {profile?.city?.trim() && donationsVisible.length > 0 ? (
        <View className="mt-10 px-5">
          <View className="mb-4 flex-row items-center justify-between">
            <Text className="text-sm font-bold text-zinc-900 dark:text-zinc-100">İyilik Vitrini</Text>
 <Text className="text-lg">💝</Text>
          </View>
          <View className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <Text className="mb-3 px-1 text-xs leading-5 text-zinc-700 dark:text-zinc-300">
              Ücretsiz sahiplendirme ilanları — ihtiyacı olan ailelerle paylaş.
            </Text>
            <FlatList
              horizontal
              data={donationsVisible}
              keyExtractor={d => d.id}
              showsHorizontalScrollIndicator={false}
              nestedScrollEnabled
              contentContainerStyle={{ paddingVertical: 4, paddingRight: 8 }}
              renderItem={({ item: d }) => (
                <TouchableOpacity
                  activeOpacity={0.9}
                  onPress={() => {
                    void impactLight();
                    tabNavigation.navigate('Community', { screen: 'CommunityMarketplace' });
                  }}
                  className="mr-3 w-44 overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
                  {d.images?.[0] ? (
                    <Image
                      source={{ uri: cloudinaryDisplayUrl(d.images[0], 400) }}
 className="h-24 w-full"
                      resizeMode="cover"
                    />
                  ) : (
                    <View className="h-24 w-full items-center justify-center bg-zinc-100 dark:bg-zinc-800">
 <Text className="text-2xl">🎁</Text>
                    </View>
                  )}
                    <View className="p-4">
                    <Text
 className="text-xs font-bold text-zinc-900 dark:text-zinc-100"
                      numberOfLines={2}>
                      {d.title}
                    </Text>
 <Text className="mt-1 text-[10px] font-semibold text-zinc-600 dark:text-zinc-400">
                      Ücretsiz sahiplendirme
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      ) : null}

        <View className="mt-10 px-5">
          <Text className="mb-4 text-sm font-bold text-zinc-900 dark:text-zinc-100">
            Topluluktan son sorular
          </Text>
        {feedPosts.length === 0 ? (
 <View className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-sm text-gray-500 dark:text-zinc-400">
              Henüz öne çıkan soru yok. Dert Köşesi&apos;nden topluluğa katıl.
            </Text>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => tabNavigation.navigate('Forum')}
              className="mt-3 self-start rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800">
 <Text className="text-xs font-semibold text-zinc-700 dark:text-zinc-400">
                Dert Köşesi&apos;ne git
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
 <View className="gap-2">
            {feedPosts.slice(0, 3).map(post => (
              <TouchableOpacity
                key={post.id}
                activeOpacity={0.88}
                onPress={() =>
                  tabNavigation
                    .getParent<NativeStackNavigationProp<MainStackParamList>>()
                    ?.navigate('ForumPostDetail', { postId: post.id })
                }
 className="rounded-2xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="mb-1 flex-row items-center justify-between">
                  <View className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 dark:border-zinc-700 dark:bg-zinc-800">
                    <Text className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
                      {post.category}
                    </Text>
                  </View>
 <Text className="text-xs text-gray-400 dark:text-zinc-500">
                    {formatFeedPostDate(post.createdAt)}
                  </Text>
                </View>
                <Text
 className="text-sm font-bold text-gray-800 dark:text-zinc-100"
                  numberOfLines={2}>
                  {post.title}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

        <View className="mt-10 px-5 pb-2">
          <View className="mb-4 flex-row items-center justify-between">
            <Text className="text-sm font-bold text-zinc-900 dark:text-zinc-100">Yaklaşan buluşmalar</Text>
          <TouchableOpacity
            onPress={() =>
              tabNavigation.navigate('Community', { screen: 'CommunityEvents', params: {} })
            }
            activeOpacity={0.8}>
            <Text className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">Tümü</Text>
          </TouchableOpacity>
        </View>
        {upcomingEventsVisible.length === 0 ? (
 <View className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-sm text-gray-500 dark:text-zinc-400">
              Yakında etkinlik yok veya profilinde il bilgisi eksik. Topluluk sekmesinden göz at.
            </Text>
          </View>
        ) : (
 <View className="gap-2">
            {upcomingEventsVisible.map(ev => (
              <TouchableOpacity
                key={ev.id}
                activeOpacity={0.88}
                onPress={() =>
                  tabNavigation.navigate('Community', {
                    screen: 'CommunityEvents',
                    params: { focusEventId: ev.id },
                  })
                }
                className="rounded-2xl border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                <Text
 className="text-sm font-bold text-gray-800 dark:text-zinc-100"
                  numberOfLines={1}>
                  {ev.title}
                </Text>
 <Text className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  {formatFeedEventWhen(ev.eventAt)}
                </Text>
                <Text
 className="mt-0.5 text-xs text-gray-400 dark:text-zinc-500"
                  numberOfLines={1}>
                  {ev.venueName}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );
}
